import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { toastrError } from '@portals/redux/actions/toastr';
import {
  PaginatedQueryParamsType,
  PaginationResponse,
  UsePaginatedTableApiQuery,
} from '@portals/types';

import { PartnerInvoiceType } from './invoices.types';
import { ServerError } from '../../types';
import {
  buildUrlFromFilters,
  fetchApiRequest,
  useRequestOptions,
} from '../../utils';
import { usePaginatedTableApiQuery } from '../../utils/paginated-table';
import { ordersQueryKeys } from '../orders';

export const INVOICES_API_URL = 'ui/partner/invoices';

const invoicesQueryKeys = {
  all: [INVOICES_API_URL, 'partnerInvoices'],
  list: () => [...invoicesQueryKeys.all, 'list'],
};

export const useInvoices = (
  params: PaginatedQueryParamsType<PartnerInvoiceType>
) => {
  const { url, options } = useRequestOptions({ url: INVOICES_API_URL });

  const requestUrl = buildUrlFromFilters<PartnerInvoiceType>({
    url,
    ...params,
  });

  return useQuery<PaginationResponse<PartnerInvoiceType>>({
    queryKey: [...invoicesQueryKeys.all, params],
    queryFn: () => fetchApiRequest(requestUrl, options),
  });
};

export function useInvoicesTableData(
  tableState: UsePaginatedTableApiQuery<PartnerInvoiceType>['tableState'],
  columns: UsePaginatedTableApiQuery<PartnerInvoiceType>['columns'],
  baseUrl = INVOICES_API_URL
) {
  return usePaginatedTableApiQuery<PartnerInvoiceType>({
    baseUrl,
    tableState,
    columns,
    queryKey: [...invoicesQueryKeys.list(), baseUrl, tableState],
  });
}

export const useUpdateInvoicePartnerNote = () => {
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: INVOICES_API_URL,
    method: 'PATCH',
  });

  return useMutation({
    mutationFn: ({ note, invoiceId }: { invoiceId: string; note: string }) =>
      fetchApiRequest(`${url}/${invoiceId}`, {
        ...options,
        body: JSON.stringify({ partner_note: note }),
      }),
    onSuccess: async () => {
      await queryClient.refetchQueries(invoicesQueryKeys.all);
    },
  });
};

export const useUploadInvoice = (invoiceId: string) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { url, options } = useRequestOptions({
    url: `${INVOICES_API_URL}/${invoiceId}`,
    method: 'PATCH',
  });

  return useMutation({
    mutationFn: (fileUrl: string) =>
      fetchApiRequest(url, {
        ...options,
        body: JSON.stringify({ invoice_file_url: fileUrl }),
      }),
    onSuccess: async () => {
      queryClient.refetchQueries(invoicesQueryKeys.all);
      queryClient.refetchQueries(ordersQueryKeys.all);
    },
    onError: () =>
      dispatch(toastrError('Failed to upload Receipt file. Please try again')),
  });
};

interface UseExtendInvoiceExpirationTimeParams {
  invoiceId: string;
  extensionPeriodDays: number;
}

export function useExtendInvoiceExpirationTime() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: INVOICES_API_URL,
    method: 'PATCH',
  });

  return useMutation<void, ServerError, UseExtendInvoiceExpirationTimeParams>({
    mutationFn: ({ invoiceId, extensionPeriodDays }) =>
      fetchApiRequest(`${url}/${invoiceId}/extend_expiration`, {
        ...options,
        body: JSON.stringify({ extension_period_days: extensionPeriodDays }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(invoicesQueryKeys.all);
    },
    onError: ({ error }) => dispatch(toastrError(error)),
  });
}
